/**
 * Help data. Pages and What's new come from the client layer (built in); each user's What's new state,
 * reference documents and support reports are read under RLS; uploads and reports go through the API's /help.
 */
import { entryKey, HELP_ROUTES, pagesFor, unseenSince, type HelpPage, type WhatsNewEntry } from "@ops/client-config/help";
export { entryKey };
import { helpBlob, helpFetch, recentApiErrors } from "../../lib/api";
import { BRAND } from "../../lib/config";
import { PREVIEW } from "../../lib/preview";
import { supabase } from "../../lib/supabase";

export type Who = "office" | "crew";
export type { HelpPage, WhatsNewEntry };

const check = <T>(r: { data: T | null; error: { message: string } | null }): T => {
  if (r.error) throw new Error(r.error.message);
  return r.data as T;
};

/* ---------- pages ---------- */

export const helpPages = (who: Who): HelpPage[] => pagesFor(BRAND.help.pages, who);
export const helpPage = (id: string, who: Who): HelpPage | undefined => helpPages(who).find((p) => p.id === id);
export const whatsNew = (): WhatsNewEntry[] => BRAND.help.whatsNew;

/** The guide for a screen: the page named after it (e.g. "jobs" for #/jobs), else the first for that route. */
export function pageForRoute(route: string, who: Who): HelpPage | undefined {
  const pages = helpPages(who).filter((p) => p.route === route && p.id !== "whats-new");
  const slug = route.replace(/^#\//, "");
  return pages.find((p) => p.id === slug) ?? pages.find((p) => p.id !== "troubleshooting" && p.id !== "getting-started") ?? pages[0];
}

/** Other pages for the same screen, and pages this one links to. */
export function relatedPages(page: HelpPage, who: Who): HelpPage[] {
  const all = helpPages(who);
  const linked = [...page.body.matchAll(/\]\(help:([a-z0-9-]+)\)/g)].map((m) => m[1]);
  return all.filter((p) => p.id !== page.id && (p.route === page.route || linked.includes(p.id)));
}

/** Getting started first, then by area (screen order), then title. */
export function groupedPages(who: Who, q = ""): { area: string; pages: HelpPage[] }[] {
  const needle = q.trim().toLowerCase();
  const pages = helpPages(who).filter((p) => !needle || [p.title, p.summary, p.body].some((s) => s.toLowerCase().includes(needle)));
  const order = (r: string) => {
    const i = (HELP_ROUTES as readonly string[]).indexOf(r);
    return i < 0 ? HELP_ROUTES.length : i;
  };
  const first = pages.filter((p) => p.id === "getting-started");
  const rest = pages.filter((p) => p.id !== "getting-started").sort((a, b) => order(a.route) - order(b.route) || a.title.localeCompare(b.title));
  const out: { area: string; pages: HelpPage[] }[] = [];
  if (first.length) out.push({ area: "Start here", pages: first });
  for (const p of rest) {
    const area = AREA[p.route] ?? "Other";
    const g = out.find((x) => x.area === area);
    if (g) g.pages.push(p);
    else out.push({ area, pages: [p] });
  }
  return out;
}

const AREA: Record<string, string> = {
  "#/today": "Every day",
  "#/my": "On site",
  "#/leads": "Winning work",
  "#/clients": "Winning work",
  "#/quotes": "Winning work",
  "#/jobs": "Running work",
  "#/bookings": "Running work",
  "#/crew": "Running work",
  "#/equipment": "Running work",
  "#/incidents": "Safety",
  "#/invoices": "Accounts",
  "#/pay": "Accounts",
  "#/rates": "Settings",
  "#/terms": "Settings",
  "#/crm": "Settings",
  "#/integrations": "Settings",
  "#/help": "Every day",
};

/* ---------- What's new seen state (per user, in the database) ---------- */

async function me(): Promise<{ id: string; org: string } | null> {
  const { data: s } = await supabase.auth.getSession();
  const id = s.session?.user.id;
  if (!id) return null;
  const { data } = await supabase.from("profiles").select("organisation_id").eq("id", id).maybeSingle();
  return data ? { id, org: data.organisation_id as string } : null;
}

/** The What's new entries this user has not seen, newest first. */
export async function unseenWhatsNew(): Promise<WhatsNewEntry[]> {
  const entries = whatsNew();
  if (!entries.length) return [];
  if (PREVIEW) return entries.slice(0, 2);
  const u = await me();
  if (!u) return [];
  const row = check(await supabase.from("help_seen").select("whats_new_seen").eq("profile_id", u.id).maybeSingle()) as { whats_new_seen: string | null } | null;
  return unseenSince(entries, row?.whats_new_seen ?? null);
}

export async function markWhatsNewSeen(key: string): Promise<void> {
  if (PREVIEW) return;
  const u = await me();
  if (!u) return;
  check(await supabase.from("help_seen").upsert({ profile_id: u.id, organisation_id: u.org, whats_new_seen: key }, { onConflict: "profile_id" }));
}

/* ---------- reference documents ---------- */

export interface HelpDocument { id: string; title: string; audience: "office" | "crew" | "all"; file_name: string; uploaded_at: string }

export async function listDocuments(): Promise<HelpDocument[]> {
  if (PREVIEW) return [];
  return check(await supabase.from("help_documents").select("id, title, audience, file_name, uploaded_at").is("removed_at", null).order("title")) as HelpDocument[];
}
export const documentFile = (id: string) => helpBlob(`/documents/${id}/file`, { timeoutMs: 60_000 });
export function uploadDocument(title: string, audience: string, file: File) {
  const f = new FormData();
  f.set("title", title);
  f.set("audience", audience);
  f.set("file", file);
  return helpFetch<{ document: { id: string } }>("/documents", { method: "POST", body: f }, { timeoutMs: 120_000 });
}
export const removeDocument = (id: string) => helpFetch(`/documents/${id}/remove`, { method: "POST" });

/* ---------- support reports ---------- */

export interface SupportReport {
  id: string; reporter_name: string | null; reporter_email: string | null; role: string; route: string | null; platform_version: string | null; user_agent: string | null;
  trying: string; happened: string | null; api_errors: { status: number; path: string; message: string }[]; screenshot_key: string | null;
  status: "open" | "answered" | "fixed"; reply: string | null; replied_at: string | null; created_at: string; profile_id: string | null;
}
export const REPORT_STATUS_LABEL: Record<SupportReport["status"], string> = { open: "Open", answered: "Answered", fixed: "Fixed" };

/** Reports the user can see: their own, or all of them for a director (RLS decides). */
export async function listReports(): Promise<SupportReport[]> {
  if (PREVIEW) return [];
  return check(await supabase.from("support_reports").select("*").order("created_at", { ascending: false }).limit(200)) as SupportReport[];
}

export async function sendReport(r: { trying: string; happened: string; route: string; screenshot: Blob | null }) {
  const f = new FormData();
  f.set("trying", r.trying);
  f.set("happened", r.happened);
  f.set("route", r.route);
  f.set("localTime", new Date().toString().slice(0, 33));
  f.set("apiErrors", JSON.stringify(recentApiErrors().map(({ status, path, message }) => ({ status, path, message }))));
  if (r.screenshot) f.set("screenshot", r.screenshot, r.screenshot instanceof File ? r.screenshot.name : "screenshot.png");
  return helpFetch<{ report: { id: string }; message: string }>("/reports", { method: "POST", body: f }, { timeoutMs: 60_000 });
}
export const reportScreenshot = (id: string) => helpBlob(`/reports/${id}/screenshot`);
export const replyToReport = (id: string, status: SupportReport["status"], reply: string | null) => helpFetch(`/reports/${id}/reply`, { method: "POST", body: JSON.stringify({ status, reply }) });

/**
 * A screenshot of the current tab, where the browser can take one (desktop Chrome, Edge and Firefox ask the user
 * to share the tab). Null when it can't or the user says no; on a phone, attach a photo instead.
 */
export async function captureScreen(): Promise<Blob | null> {
  const md = navigator.mediaDevices as MediaDevices & { getDisplayMedia?: (o: unknown) => Promise<MediaStream> };
  if (!md?.getDisplayMedia) return null;
  let stream: MediaStream | null = null;
  try {
    stream = await md.getDisplayMedia({ video: { displaySurface: "browser" }, audio: false, preferCurrentTab: true, selfBrowserSurface: "include" });
    const video = document.createElement("video");
    video.srcObject = stream;
    video.muted = true;
    await video.play();
    await new Promise((r) => setTimeout(r, 250));
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")!.drawImage(video, 0, 0);
    return await new Promise<Blob | null>((res) => canvas.toBlob((b) => res(b), "image/png"));
  } catch {
    return null;
  } finally {
    stream?.getTracks().forEach((t) => t.stop());
  }
}
export const canCaptureScreen = () => Boolean((navigator.mediaDevices as MediaDevices & { getDisplayMedia?: unknown } | undefined)?.getDisplayMedia) && !/Android|iPhone|iPad/i.test(navigator.userAgent);
